import React, { useState, useMemo } from 'react';
import { Crud } from '@unicus/react-ui-kit';
import { Button } from 'antd';
import { AccountAccessDenied } from 'components';
import moment from 'moment';
import { fetch } from 'stores/financialEntries/financialEntryActions';
import useAuth from 'core/hooks/useAuth';
import { useSelector } from 'react-redux';
import {
  ROLE_CLINIC_ADMINISTRATOR,
  ROLE_FINANCIAL,
  ACCOUNT_TYPE_STUDENT
} from 'constants/types';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';
import FinancialEntryHeader from './FinancialEntryHeader';
import FinancialEntryAccountantAlert from './FinancialEntryAccountantAlert';
import FinancialEntryListTable from './FinancialEntryListTable';
import FinancialEntryEditManager from './FinancialEntryEditManager';
import FinancialEntryTransferDrawer from './FinancialEntryTransferDrawer';

const defaultParams = {
  dateType: 'creationTime',
  initialDate: moment()
    .startOf('month')
    .format('YYYY/MM/DD'),
  endDate: moment()
    .endOf('month')
    .format('YYYY/MM/DD'),
  launchStatus: ['active'],
  itemStatus: ['awaitingPayment', 'paid']
};

const FinancialEntryList = ({
  fixedQueryParams,
  defaultQueryParams = defaultParams,
  disableAccountingAlert = false,
  disableEditing = false,
  enableSelectRow = false,
  mode = 'all',
  getCheckboxProps,
  initialValuesOfCreation = {},
  suggestAsDefaultCheckingAccount = null,
  hiddenButtonActions = [],
  onConfirmSelect,
  titleOfSelectedConfirmButton = 'Confirmar',
  useQueryStringParams = true,
  hideSummary = false,
  isBankReconciliation = false,
  loadingSelection = false,
  ...props
}) => {
  const ref = React.useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const { user, hasRole } = useAuth();
  const { serviceProvider } = user;
  const { openTransfer } = useSelector(
    (c) => c.resources['financial-entries'] ?? {}
  );

  const getFixedQueryParams = () => {
    if (!hasRole(ROLE_FINANCIAL, ROLE_CLINIC_ADMINISTRATOR)) {
      return {
        serviceProviderIds: [serviceProvider?.id],
        ...(fixedQueryParams ?? {})
      };
    }

    return fixedQueryParams;
  };

  const onSelectChange = (record, selected) => {
    if (selected) {
      setSelectedRows([...selectedRows, record]);
    } else {
      setSelectedRows(selectedRows.filter((c) => c.id !== record.id));
    }
  };

  const onSelectAllChange = (selected, _, rows) => {
    if (selected) {
      setSelectedRows([...selectedRows, ...rows]);
    } else {
      setSelectedRows(
        selectedRows.filter((c) => !rows.map((d) => d.id).includes(c.id))
      );
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedRows.map((c) => c.id),
    onSelect: onSelectChange,
    onSelectAll: onSelectAllChange,
    getCheckboxProps
  };

  const handleConfirmSelect = () => {
    if (onConfirmSelect) {
      onConfirmSelect(selectedRows);
    }
  };

  const Header = useMemo(
    () => (headerProps) => {
      const buttons = enableSelectRow ? (
        <Button
          className="gx-ml-2"
          type="primary"
          onClick={handleConfirmSelect}
          disabled={selectedRows.length === 0}
          loading={loadingSelection}
        >
          {titleOfSelectedConfirmButton}
        </Button>
      ) : null;

      return (
        <FinancialEntryHeader
          {...headerProps}
          initialValuesOfCreation={initialValuesOfCreation}
          disableEditing={disableEditing}
          checkingAccountIds
          mode={mode}
          customButtons={() => buttons}
          hideSummary={hideSummary}
          fixedQueryParams={getFixedQueryParams()}
          defaultQueryParams={defaultQueryParams}
          isBankReconciliation={isBankReconciliation}
        />
      );
    },
    [enableSelectRow, selectedRows, loadingSelection]
  );

  const handleOnBuildQueryParams = (queryParams) => {
    return {
      ...defaultQueryParams,
      ...queryParams
    };
  };

  const FinancialEntryEditManagerMemo = useMemo(
    () => (props) => {
      return (
        <FinancialEntryEditManager
          suggestAsDefaultCheckingAccount={suggestAsDefaultCheckingAccount}
          {...props}
        />
      );
    },
    [suggestAsDefaultCheckingAccount]
  );

  return (
    <AccountAccessDenied
      title="Assinatura de estudante não permite acesso ao financeiro!"
      types={[ACCOUNT_TYPE_STUDENT]}
    >
      {!disableAccountingAlert && <FinancialEntryAccountantAlert />}
      {openTransfer ? <FinancialEntryTransferDrawer /> : null}

      <ReactToPrint
        content={() => ref.current}
        documentTitle="Movimentações"
        removeAfterPrint={false}
      >
        <div ref={ref}>
          <style type="text/css" media="print">
            {'@page { size: landscape; }'}
          </style>
          <Crud
            resource="financial-entries"
            fetch={fetch}
            headerComponent={Header}
            editComponent={FinancialEntryEditManagerMemo}
            fixedQueryParams={getFixedQueryParams()}
            useQueryStringParams={useQueryStringParams}
            onBuildQueryParams={handleOnBuildQueryParams}
          >
            <FinancialEntryListTable
              disableEditing={disableEditing}
              rowSelection={!enableSelectRow ? undefined : rowSelection}
              hiddenButtonActions={hiddenButtonActions}
            />
          </Crud>
        </div>
      </ReactToPrint>
    </AccountAccessDenied>
  );
};

export default FinancialEntryList;
